import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/root-reducer';
import toaster, { Theme } from '../../../../shared/toaster';
import {
  LeadRevealType,
  BULK_ACTION_TIMEOUT,
  LeadFinderSecondaryTab,
  PEOPLE_MAX_POLLING_LIMIT,
} from '../../types';
import {
  INSUFFICIENT_CREDITS_ERROR_MESSAGE,
  LEADS_PER_PAGE,
  SAVED_TAB_BULK_OPERATION_LIMIT,
  WORK_EMAIL_REVEAL_TYPES,
  PHONE_REVEAL_TYPES,
  getV2SavedTabPageSize,
  isInsufficientCreditsError,
} from '../../helpers/helper';
import { leadFinderBulkOperationLimit } from '../../../../shared/utils/user-details';
import {
  bulkRevealLeadAPI,
  getLeadsV2,
  getSavedLeads,
} from '../../helpers/leads.api';
import {
  enableBulkActionRequestPolling,
  disableBulkActionRequestPolling,
  setFirebaseProgressNotification,
  clearFirebaseProgressNotification,
  triggerFetchUnreadCount,
} from '../../../home/home-slice';
import { SystemErrors } from '../../../../shared/enums/system-errors';
import { getPostLoadMetaRequest } from '../../../home/extra-actions';
import {
  useLeadFinderRealtimeNotification,
  LeadFinderNotificationDoc,
} from './useLeadFinderRealtimeNotification';

const FIREBASE_SEARCH_API_THROTTLE_MS = 3000;

// Firestore error codes that are recoverable. The listener hook re-subscribes
// once on these; if it still fails afterwards, handleFirebaseError ignores
// them rather than tearing down the realtime tracking and falling back to polling.
const TRANSIENT_FIREBASE_ERROR_CODES = new Set([
  'cancelled',
  'unavailable',
  'deadline-exceeded',
  'internal',
  'aborted',
  'unknown',
]);

// Maps frontend revealType to backend notification type string
// (mirrors lead-finder-v2.service.ts createNotificationForLeadFinderBulkOperation)
const getNotificationTypeForReveal = (
  revealType: LeadRevealType,
): string => {
  switch (revealType) {
    case LeadRevealType.Phone:
      return 'lead-reveal-v2-phone-async';
    case LeadRevealType.EmailPhone:
      return 'lead-reveal-v2-email-phone-async';
    case LeadRevealType.PersonalEmail:
      return 'lead-reveal-v2-personal-email-async';
    case LeadRevealType.PersonalEmailPhone:
      return 'lead-reveal-v2-personal-email-phone-async';
    case LeadRevealType.EmailPersonalEmail:
      return 'lead-reveal-v2-email-personal-email-async';
    case LeadRevealType.EmailPersonalEmailPhone:
      return 'lead-reveal-v2-email-personal-email-phone-async';
    case LeadRevealType.Email:
    default:
      return 'lead-reveal-v2-email-async';
  }
};

interface UseV2LeadRevealProps {
  selectedLeads: number[];
  isSelectedAllLeads: boolean;
  deSelectedLeads: number[];
  leadsData: {
    profiles: any[];
    pagination: any;
  };
  handleGeneratePayload: () => any;
  setLeadsData: React.Dispatch<
    React.SetStateAction<{
      profiles: any[];
      pagination: any;
    }>
  >;
  setEmailRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  setEmailPhoneRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  emailRevealingLeads: Record<number, boolean>;
  emailPhoneRevealingLeads: Record<number, boolean>;
  setPersonalEmailRevealingLeads: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  personalEmailRevealingLeads: Record<number, boolean>;
  setIsSelectedAllLeads: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  setDeSelectedLeads: React.Dispatch<React.SetStateAction<number[]>>;
  activeSubTab?: any;
  apiParam?: any;
  savedApiParam?: any;
  isLoading?: boolean;
}

export const useV2LeadReveal = ({
  selectedLeads,
  isSelectedAllLeads,
  deSelectedLeads,
  leadsData,
  handleGeneratePayload,
  setLeadsData,
  setEmailRevealingLeads,
  setEmailPhoneRevealingLeads,
  setPersonalEmailRevealingLeads,
  personalEmailRevealingLeads,
  setIsSelectedAllLeads,
  setSelectedLeads,
  setDeSelectedLeads,
  activeSubTab,
  apiParam,
  savedApiParam,
  isLoading = false,
}: UseV2LeadRevealProps) => {
  const dispatch = useDispatch();
  // User-applied cap on how many records a select-all reveal processes.
  const peopleMaxLeadsLimit = useSelector(
    (state: RootState) => state.leadFinderV2.selection.peopleMaxLeadsLimit,
  );
  const leadFinderLimit = leadFinderBulkOperationLimit();
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [isFirstPollRequest, setIsFirstPollRequest] = useState(true);
  const pollingAttemptsRef = useRef(0);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const hasRevealingLeadsRef = useRef(false);
  const lastPollingResponseRef = useRef<any>(null);
  const leadsDataRef = useRef(leadsData);
  const previousSubTabRef = useRef(activeSubTab);
  const pollingPageRef = useRef<number | null>(null);
  const pollingFiltersRef = useRef<string | null>(null);
  const isStartingPollingRef = useRef(false);
  const processedCombinationsRef = useRef<Set<string>>(new Set());
  const previousIsLoadingRef = useRef<boolean>(false);
  const [
    leadFinderRevealType,
    setLeadFinderRevealType,
  ] = useState<LeadRevealType>(LeadRevealType.Email);
  const [
    selectAllRevealType,
    setSelectAllRevealType,
  ] = useState<LeadRevealType | null>(null);

  // Firebase real-time notification state
  // Initialize from sessionStorage so Firebase resumes after page refresh
  const [firebaseNotificationUuIds, setFirebaseNotificationUuIds] = useState<
    Set<string>
  >(() => {
    const stored = sessionStorage.getItem('lf_firebase_notification_uuids');
    return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
  });
  const lastFirebaseSearchApiCallRef = useRef<number>(0);
  const lastFirebaseCompletedCountsRef = useRef<Map<string, number>>(
    new Map(),
  );
  const hasRevealingLeadsOnCurrentPageRef = useRef(false);
  // Tracks uuids we've already registered with Firebase tracking so duplicate
  // calls to addFirebaseNotification (e.g. from both listManagementUtils and
  // handleAddToList) don't fire two initial search-API refetches.
  const registeredFirebaseUuIdsRef = useRef<Set<string>>(new Set());

  // Sync firebaseNotificationUuIds to sessionStorage
  useEffect(() => {
    if (firebaseNotificationUuIds.size > 0) {
      sessionStorage.setItem(
        'lf_firebase_notification_uuids',
        JSON.stringify(Array.from(firebaseNotificationUuIds)),
      );
    } else {
      sessionStorage.removeItem('lf_firebase_notification_uuids');
    }
  }, [firebaseNotificationUuIds]);

  // Keep ref in sync with leadsData to avoid stale closures
  useEffect(() => {
    leadsDataRef.current = leadsData;
    // Update page-awareness ref: does current page have revealing leads?
    const profiles = leadsData?.profiles || [];
    hasRevealingLeadsOnCurrentPageRef.current = profiles.some(
      (lead) => lead.isRevealing === true || lead.isPersonalEmailRevealing === true,
    );
  }, [leadsData]);

  useEffect(() => {
    const previousTab = previousSubTabRef.current;
    previousSubTabRef.current = activeSubTab;

    if (previousTab && previousTab !== activeSubTab && (isPollingEnabled || firebaseNotificationUuIds.size > 0)) {
      setIsPollingEnabled(false);
      // Keep firebaseNotificationUuIds and lastFirebaseCompletedCountsRef intact
      // so the Firestore listener keeps streaming progress for in-flight reveals
      // across sub-tab switches. The progress bar in the header is global.
      setEmailRevealingLeads({});
      setEmailPhoneRevealingLeads({});
      setPersonalEmailRevealingLeads({});
      setIsSelectedAllLeads(false);
      setSelectedLeads([]);
      setDeSelectedLeads([]);
      setSelectAllRevealType(null);
      hasRevealingLeadsRef.current = false;
      hasRevealingLeadsOnCurrentPageRef.current = false;
      pollingAttemptsRef.current = 0;
      lastPollingResponseRef.current = null;
      pollingPageRef.current = null;
      pollingFiltersRef.current = null;
      lastFirebaseSearchApiCallRef.current = 0;
      // Reset tracking so we can check the new tab
      processedCombinationsRef.current.clear();
      isStartingPollingRef.current = false;
    }
  }, [
    activeSubTab,
    isPollingEnabled,
    setIsPollingEnabled,
    setDeSelectedLeads,
    setEmailPhoneRevealingLeads,
    setEmailRevealingLeads,
    setPersonalEmailRevealingLeads,
    setIsSelectedAllLeads,
    setSelectedLeads,
    setSelectAllRevealType,
  ]);

  // Cancel polling when page or filters change
  useEffect(() => {
    if (
      isPollingEnabled &&
      pollingPageRef.current !== null &&
      pollingFiltersRef.current !== null
    ) {
      const filtersPayload = handleGeneratePayload();
      const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
      const currentPage = isSaved
        ? Math.ceil(
            (savedApiParam?.start || 1) /
              (savedApiParam?.take || getV2SavedTabPageSize()),
          )
        : apiParam?.page ?? 1;

      const currentFiltersStr = JSON.stringify(filtersPayload);
      const isPageMatch = currentPage === pollingPageRef.current;
      const isFilterMatch = currentFiltersStr === pollingFiltersRef.current;

      // Cancel polling if page or filters changed
      if (!isPageMatch || !isFilterMatch) {
        setIsPollingEnabled(false);
        setEmailRevealingLeads({});
        setEmailPhoneRevealingLeads({});
        setPersonalEmailRevealingLeads({});
        setIsSelectedAllLeads(false);
        setSelectAllRevealType(null);
        hasRevealingLeadsRef.current = false;
        pollingAttemptsRef.current = 0;
        lastPollingResponseRef.current = null;
        pollingPageRef.current = null;
        pollingFiltersRef.current = null;
        // Reset tracking so we can check the new page/filter
        processedCombinationsRef.current.clear();
        isStartingPollingRef.current = false;
      }
    }
  }, [
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    setIsPollingEnabled,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    setPersonalEmailRevealingLeads,
    setIsSelectedAllLeads,
  ]);

  const handleFetchLeadsAfterBulkActions = useCallback(
    async (enablePolling = false, newPolling = false) => {
      try {
        if (newPolling) {
          const filtersPayload = handleGeneratePayload();
          const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
          const paginationPayload = isSaved ? savedApiParam : apiParam;
          const payload = {
            ...paginationPayload,
            ...filtersPayload,
          };

          // Store the polling context when starting a new poll request
          const pollingPage = isSaved
            ? Math.ceil(
                (savedApiParam?.start || 1) /
                  (savedApiParam?.take || getV2SavedTabPageSize()),
              )
            : apiParam?.page ?? 1;

          pollingPageRef.current = pollingPage;
          pollingFiltersRef.current = JSON.stringify(filtersPayload);

          // Call appropriate API based on active sub-tab
          let res;
          if (isSaved) {
            // For saved tab, use getSavedLeads API
            res = await getSavedLeads(payload);
          } else {
            // For total/netNew tabs, use getLeadsV2 API
            const params = new URLSearchParams();
            params.set('tab', activeSubTab || 'total');
            params.set('_nr', '1');
            res = await getLeadsV2(params.toString(), payload);
          }

          // Store the last response to use when polling stops
          lastPollingResponseRef.current = res;

          // Extract profiles from response
          const resolvedProfiles = Array.isArray(res?.payload)
            ? res?.payload
            : res?.payload?.profiles || [];

          if (resolvedProfiles?.length) {
            // Check if there are any revealing leads in the current response
            const hasRevealingLeads = resolvedProfiles.some(
              (lead) => lead.isRevealing || lead.isPersonalEmailRevealing,
            );
            hasRevealingLeadsRef.current = hasRevealingLeads;

            // VALIDATION: Check if the response matches current page/filters before updating
            const currentFiltersPayload = handleGeneratePayload();
            const validationCurrentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch =
              validationCurrentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            // Only update leads data if we're still on the same page and filters
            if (!isPageMatch || !isFilterMatch) {
              // Don't update the data, but keep the response for potential use
              return;
            }

            // Update revealing leads state based on isRevealing status from response
            // Use REVEAL_TYPE arrays instead of exact matches because revealType
            // is cumulative (e.g. Email→EmailPersonalEmail after multiple reveals)
            const revealingEmailLeads = resolvedProfiles.filter(
              (lead) =>
                lead.isRevealing &&
                lead?.revealType !== undefined &&
                WORK_EMAIL_REVEAL_TYPES.includes(lead.revealType) &&
                !PHONE_REVEAL_TYPES.includes(lead.revealType),
            );
            const revealingEmailPhoneLeads = resolvedProfiles.filter(
              (lead) =>
                lead.isRevealing &&
                lead?.revealType !== undefined &&
                PHONE_REVEAL_TYPES.includes(lead.revealType),
            );
            const revealedLeads = resolvedProfiles.filter(
              (lead) => lead.isRevealed && lead?.isRevealing === false,
            );

            // Update email revealing leads state
            const updateLoadingEmailLeads: [number, boolean][] = [];
            updateLoadingEmailLeads.push(
              ...revealingEmailLeads.map((lead) => [lead.id, true]),
            );
            updateLoadingEmailLeads.push(
              ...revealedLeads
                .filter(
                  (lead) =>
                    lead?.revealType !== undefined &&
                    WORK_EMAIL_REVEAL_TYPES.includes(lead.revealType),
                )
                .map((lead) => [lead.id, false]),
            );
            setEmailRevealingLeads((prev) => {
              // Preserve any leads that are currently revealing but not in the API response
              // This handles the case where a lead was just revealed but isn't on the current page
              const preservedRevealingLeads = Object.entries(prev)
                .filter(
                  ([id, isRevealing]) =>
                    isRevealing &&
                    !updateLoadingEmailLeads.some(
                      ([updateId]) => String(updateId) === String(id),
                    ),
                )
                .map(([id]) => [Number(id), true] as [number, boolean]);

              const allUpdates = [
                ...updateLoadingEmailLeads,
                ...preservedRevealingLeads,
              ];
              return {
                ...prev,
                ...Object.fromEntries(allUpdates),
              };
            });

            // Update email phone revealing leads state
            const updateLoadingEmailPhoneLeads: [number, boolean][] = [];
            updateLoadingEmailPhoneLeads.push(
              ...revealingEmailPhoneLeads.map((lead) => [lead.id, true]),
            );
            updateLoadingEmailPhoneLeads.push(
              ...revealedLeads
                .filter(
                  (lead) =>
                    lead?.revealType !== undefined &&
                    PHONE_REVEAL_TYPES.includes(lead.revealType),
                )
                .map((lead) => [lead.id, false]),
            );
            setEmailPhoneRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(updateLoadingEmailPhoneLeads),
            }));

            // Update personal email revealing leads state
            const revealingPersonalEmailLeads = resolvedProfiles.filter(
              (lead) => lead.isPersonalEmailRevealing,
            );
            const updateLoadingPersonalEmailLeads: [number, boolean][] = [];
            updateLoadingPersonalEmailLeads.push(
              ...revealingPersonalEmailLeads.map((lead) => [lead.id, true]),
            );
            updateLoadingPersonalEmailLeads.push(
              ...resolvedProfiles
                .filter((lead) => lead.isPersonalEmailRevealing === false)
                .map((lead) => [lead.id, false]),
            );
            setPersonalEmailRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(updateLoadingPersonalEmailLeads),
            }));

            // Update leadsData during polling to reflect latest isRevealing/isRevealed status
            if (isSaved) {
              const savedPayload = res?.payload || {};
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: savedPagination,
              });
            } else {
              // Use ref to get latest totalRows to avoid stale closure
              const totalRows =
                leadsDataRef.current?.pagination?.totalRows || 0;

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: {
                  page: validationCurrentPage,
                  limit: LEADS_PER_PAGE,
                  totalRows,
                },
              });
            }
          } else {
            hasRevealingLeadsRef.current = false;
          }
        }

        // Enable polling if requested
        // For newPolling: enable after API call completes (so ref is updated)
        // For enablePolling without newPolling: enable immediately (used by interval)
        if (enablePolling) {
          setIsPollingEnabled(true);
        }
      } catch (e: any) {
        toaster.error(e?.message || SystemErrors.InternalServerError);
      }
    },
    [
      handleGeneratePayload,
      activeSubTab,
      apiParam,
      savedApiParam,
      setLeadsData,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setPersonalEmailRevealingLeads,
      setIsPollingEnabled,
    ],
  );

  // Firebase real-time notification callbacks
  const handleFirebaseProgress = useCallback(
    (data: LeadFinderNotificationDoc) => {
      // Push progress data to Redux for header progress bar
      dispatch(
        setFirebaseProgressNotification({
          uuid: data.notificationUuId,
          notification: {
            uuid: data.notificationUuId,
            notificationType: data.notificationType,
            onScreen: true,
            metaData: {
              totalCount: data.totalCount,
              completedCount: data.completedCount,
              failedCount: data.failedCount,
              verifiedEmailLeads: data.verifiedEmailLeads,
              verifiedPhoneLeads: data.verifiedPhoneLeads,
              verifiedPersonalEmailLeads: data.verifiedPersonalEmailLeads,
              emailCredits: data.emailCredits,
              phoneCredits: data.phoneCredits,
              personalEmailCredits: data.personalEmailCredits,
              totalCredits: data.totalCredits,
            },
          },
        }),
      );

      // Throttled search API call — only if completedCount actually increased
      // and current page has revealing leads
      const lastCount =
        lastFirebaseCompletedCountsRef.current.get(data.notificationUuId) || 0;
      if (
        data.completedCount > lastCount &&
        hasRevealingLeadsOnCurrentPageRef.current
      ) {
        lastFirebaseCompletedCountsRef.current.set(
          data.notificationUuId,
          data.completedCount,
        );
        const now = Date.now();
        if (now - lastFirebaseSearchApiCallRef.current >= FIREBASE_SEARCH_API_THROTTLE_MS) {
          lastFirebaseSearchApiCallRef.current = now;
          handleFetchLeadsAfterBulkActions(false, true);
        }
      }
    },
    [handleFetchLeadsAfterBulkActions, dispatch],
  );

  const handleFirebaseComplete = useCallback(
    (data: LeadFinderNotificationDoc) => {
      // Update progress bar to show completion (with completedAt so header auto-closes)
      dispatch(
        setFirebaseProgressNotification({
          uuid: data.notificationUuId,
          notification: {
            uuid: data.notificationUuId,
            notificationType: data.notificationType,
            onScreen: true,
            completedAt: data.completedAt || new Date().toISOString(),
            metaData: {
              totalCount: data.totalCount,
              completedCount: data.completedCount,
              failedCount: data.failedCount,
              verifiedEmailLeads: data.verifiedEmailLeads,
              verifiedPhoneLeads: data.verifiedPhoneLeads,
              verifiedPersonalEmailLeads: data.verifiedPersonalEmailLeads,
              emailCredits: data.emailCredits,
              phoneCredits: data.phoneCredits,
              personalEmailCredits: data.personalEmailCredits,
              totalCredits: data.totalCredits,
            },
          },
        }),
      );

      // Refresh lead data immediately after reveal completes; retry once
      // after 3s if Elasticsearch lag means leads still come back as revealing.
      handleFetchLeadsAfterBulkActions(false, true).then(() => {
        if (hasRevealingLeadsRef.current) {
          setTimeout(() => {
            handleFetchLeadsAfterBulkActions(false, true);
          }, 3000);
        }
      });

      // Remove completed notification from Firebase tracking
      setFirebaseNotificationUuIds((prev) => {
        const next = new Set(prev);
        next.delete(data.notificationUuId);
        return next;
      });
      lastFirebaseCompletedCountsRef.current.delete(data.notificationUuId);

      // Refresh credit/meta info and unread notification count
      dispatch(getPostLoadMetaRequest());
      dispatch(triggerFetchUnreadCount());
    },
    [handleFetchLeadsAfterBulkActions, dispatch],
  );

  // Firebase error handler — remove failed notification from set, let polling take over.
  // Transient Firestore errors are ignored here; the listener hook handles its own
  // retry, so we only fall back to polling when the failure is permanent.
  const handleFirebaseError = useCallback(
    (notificationUuId: string, error: Error) => {
      const code = (error as { code?: string })?.code;
      if (code && TRANSIENT_FIREBASE_ERROR_CODES.has(code)) {
        return;
      }
      setFirebaseNotificationUuIds((prev) => {
        const next = new Set(prev);
        next.delete(notificationUuId);
        return next;
      });
      lastFirebaseCompletedCountsRef.current.delete(notificationUuId);
      dispatch(clearFirebaseProgressNotification(notificationUuId));
      // Enable polling so this operation is tracked by header polling
      dispatch(enableBulkActionRequestPolling());
      hasRevealingLeadsRef.current = true;
      handleFetchLeadsAfterBulkActions(true, true);
    },
    [dispatch, handleFetchLeadsAfterBulkActions],
  );

  // Firebase real-time listeners (one per active notification)
  useLeadFinderRealtimeNotification({
    notificationUuIds: firebaseNotificationUuIds,
    onProgress: handleFirebaseProgress,
    onComplete: handleFirebaseComplete,
    onError: handleFirebaseError,
  });

  // Auto-start polling when search completes and leadsData contains any revealing leads
  useEffect(() => {
    // Only check when search completes (isLoading transitions from true to false)
    const searchJustCompleted =
      previousIsLoadingRef.current === true && isLoading === false;
    previousIsLoadingRef.current = isLoading;

    // Skip if search hasn't completed or if still loading
    if (!searchJustCompleted || isLoading) {
      return;
    }

    // Skip if no leads data
    const profiles = leadsData?.profiles || [];
    if (profiles.length === 0) {
      return;
    }

    // Get current page and filters to create a unique key
    const filtersPayload = handleGeneratePayload();
    const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;
    const currentPage = isSaved
      ? Math.ceil(
          (savedApiParam?.start || 1) /
            (savedApiParam?.take || getV2SavedTabPageSize()),
        )
      : apiParam?.page ?? 1;

    const currentFiltersStr = JSON.stringify(filtersPayload);
    const combinationKey = `${currentPage}:${currentFiltersStr}:${activeSubTab}`;

    // Skip if we already processed this exact page/filter combination
    if (processedCombinationsRef.current.has(combinationKey)) {
      return;
    }

    // Mark this combination as processed
    processedCombinationsRef.current.add(combinationKey);

    // Check if leadsData has any revealing leads
    const hasRevealingLeads = profiles.some(
      (lead) => lead.isRevealing === true || lead.isPersonalEmailRevealing === true,
    );

    if (hasRevealingLeads) {
      // Update the ref to indicate we have revealing leads
      hasRevealingLeadsRef.current = true;

      // Skip polling start if Firebase is handling notifications
      if (firebaseNotificationUuIds.size > 0) {
        return;
      }

      const isPageMatch = pollingPageRef.current === currentPage;
      const isFilterMatch = pollingFiltersRef.current === currentFiltersStr;

      // Start polling if not already polling for this page/filter combination
      // and not already in the process of starting polling
      if (
        (!isPollingEnabled || !isPageMatch || !isFilterMatch) &&
        !isStartingPollingRef.current
      ) {
        // Set flag to prevent multiple simultaneous starts
        isStartingPollingRef.current = true;

        // Reset polling attempts for new polling session
        pollingAttemptsRef.current = 0;
        setIsFirstPollRequest(true);

        // Start polling by calling handleFetchLeadsAfterBulkActions
        handleFetchLeadsAfterBulkActions(true, true).catch(() => {
          // Reset flag if polling start fails
          isStartingPollingRef.current = false;
        });
      }
      return;
    }

    // Update ref if no revealing leads found
    // Only update if we're not currently polling (to avoid stopping active polling)
    if (!isPollingEnabled) {
      hasRevealingLeadsRef.current = false;
    }
  }, [
    isLoading,
    leadsData,
    isPollingEnabled,
    apiParam,
    savedApiParam,
    activeSubTab,
    handleGeneratePayload,
    handleFetchLeadsAfterBulkActions,
  ]);

  // Reset starting polling flag when polling actually starts
  useEffect(() => {
    if (isPollingEnabled) {
      isStartingPollingRef.current = false;
    }
  }, [isPollingEnabled]);

  // Keep a ref to the latest handleFetchLeadsAfterBulkActions so the polling
  // interval below always calls the current version (its closure is captured
  // once when the interval starts, but the callback identity changes on every
  // render — without the ref the interval would invoke a stale closure).
  const handleFetchLeadsAfterBulkActionsRef = useRef(
    handleFetchLeadsAfterBulkActions,
  );
  useEffect(() => {
    handleFetchLeadsAfterBulkActionsRef.current = handleFetchLeadsAfterBulkActions;
  }, [handleFetchLeadsAfterBulkActions]);

  // Polling effect
  useEffect(() => {
    const startPolling = () => {
      // Don't start a new interval if one is already running
      if (timerIdRef.current) {
        return;
      }

      // Reset polling attempts only when starting a new polling session
      pollingAttemptsRef.current = 0;

      timerIdRef.current = setInterval(async () => {
        pollingAttemptsRef.current += 1;

        const shouldContinuePolling = hasRevealingLeadsRef.current;

        // Stop if no revealing leads or if we've reached the max attempts (20)
        if (
          !shouldContinuePolling ||
          pollingAttemptsRef.current >= PEOPLE_MAX_POLLING_LIMIT
        ) {
          // Stop polling
          setIsPollingEnabled(false);
          setEmailRevealingLeads({});
          setEmailPhoneRevealingLeads({});
          setPersonalEmailRevealingLeads({});
          setIsSelectedAllLeads(false);
          setSelectedLeads([]);
          setDeSelectedLeads([]);
          setSelectAllRevealType(null);
          hasRevealingLeadsRef.current = false;
          pollingAttemptsRef.current = 0;

          // Update leadsData directly from last polling response (no need for another API call)
          if (lastPollingResponseRef.current) {
            const res = lastPollingResponseRef.current;
            const isSaved = activeSubTab === LeadFinderSecondaryTab.SAVED;

            // Validate before final update
            const currentFiltersPayload = handleGeneratePayload();
            const currentPage = isSaved
              ? Math.ceil(
                  (savedApiParam?.start || 1) /
                    (savedApiParam?.take || getV2SavedTabPageSize()),
                )
              : apiParam?.page ?? 1;

            const currentFiltersStr = JSON.stringify(currentFiltersPayload);
            const isPageMatch = currentPage === pollingPageRef.current;
            const isFilterMatch =
              currentFiltersStr === pollingFiltersRef.current;

            // Extract profiles from last polling response (same logic as during polling)
            const resolvedProfiles = Array.isArray(res?.payload)
              ? res?.payload
              : res?.payload?.profiles || [];

            if (isPageMatch && isFilterMatch && isSaved) {
              const savedPayload = res?.payload || {};
              // Use resolvedProfiles for consistency with polling logic
              const savedPaginationSource = Array.isArray(savedPayload)
                ? null
                : savedPayload?.pagination;

              const takeValue =
                savedPaginationSource?.take ||
                savedApiParam?.take ||
                getV2SavedTabPageSize();
              const startValue =
                savedPaginationSource?.start || savedApiParam?.start || 1;
              const pageNumber = Math.ceil(startValue / takeValue);

              const savedPagination = savedPaginationSource
                ? {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: savedPaginationSource.total || 0,
                  }
                : {
                    page: pageNumber,
                    limit: takeValue,
                    totalRows: resolvedProfiles.length,
                  };

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: savedPagination,
              });
            } else if (isPageMatch && isFilterMatch) {
              // Use ref to get latest totalRows to avoid stale closure
              const totalRows =
                leadsDataRef.current?.pagination?.totalRows || 0;

              setLeadsData({
                profiles: resolvedProfiles.map((lead: any) => ({ ...lead })),
                pagination: {
                  page: currentPage,
                  limit: LEADS_PER_PAGE,
                  totalRows,
                },
              });
            }

            if (isPageMatch && isFilterMatch) {
              dispatch(getPostLoadMetaRequest());
            }

            // Reset polling context
            pollingPageRef.current = null;
            pollingFiltersRef.current = null;
            lastPollingResponseRef.current = null;
          }
        } else {
          await handleFetchLeadsAfterBulkActionsRef.current(false, true);
          if (isFirstPollRequest) {
            setIsFirstPollRequest(false);
          }
        }
      }, BULK_ACTION_TIMEOUT);
    };

    const stopPolling = () => {
      setIsFirstPollRequest(true);
      pollingAttemptsRef.current = 0;
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };

    if (isPollingEnabled && firebaseNotificationUuIds.size === 0) {
      // Only start polling if not already running and Firebase is not active
      if (!timerIdRef.current) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => {
      // Only stop polling if isPollingEnabled becomes false
      if (!isPollingEnabled) {
        stopPolling();
      }
    };
  }, [
    isPollingEnabled,
    isFirstPollRequest,
    activeSubTab,
    savedApiParam,
    apiParam,
    firebaseNotificationUuIds,
  ]);

  // Single lead reveal
  const handleRevealLead = useCallback(
    async (leadId: number, revealType: LeadRevealType) => {
      try {
        setLeadFinderRevealType(revealType);

        const payload = {
          leadIds: [leadId],
          revealType,
          tagIds: [],
          newTags: [],
        };

        const revealRes = await bulkRevealLeadAPI(payload);
        if (revealRes) {
          const { message, success } = revealRes.payload || {};
          if (!success) {
            toaster.error(
              isInsufficientCreditsError(message)
                ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                : message || SystemErrors.InternalServerError,
              {
                theme: Theme.New,
              },
            );
            // Clear loading state on error
            if (
              revealType === LeadRevealType.Email ||
              revealType === LeadRevealType.EmailPersonalEmail
            ) {
              setEmailRevealingLeads((prev) => {
                const updated = { ...prev };
                delete updated[leadId];
                return updated;
              });
            }
            if (
              revealType === LeadRevealType.EmailPhone ||
              revealType === LeadRevealType.EmailPersonalEmailPhone
            ) {
              setEmailPhoneRevealingLeads((prev) => {
                const updated = { ...prev };
                delete updated[leadId];
                return updated;
              });
            }
            if (
              revealType === LeadRevealType.PersonalEmail ||
              revealType === LeadRevealType.EmailPersonalEmail ||
              revealType === LeadRevealType.EmailPersonalEmailPhone
            ) {
              setPersonalEmailRevealingLeads((prev) => {
                const updated = { ...prev };
                delete updated[leadId];
                return updated;
              });
            }
          }
          if (success) {
            toaster.success(message || '1 Lead will be revealed', {
              theme: Theme.New,
            });
            hasRevealingLeadsRef.current = true;
            // Ensure the lead is in the revealing state before starting polling
            // This is a safeguard in case the state update from handleRevealEmail hasn't committed yet
            if (revealType === LeadRevealType.Email) {
              setEmailRevealingLeads((prev) => ({
                ...prev,
                [leadId]: true,
              }));
            }
            if (revealType === LeadRevealType.EmailPhone) {
              setEmailPhoneRevealingLeads((prev) => ({
                ...prev,
                [leadId]: true,
              }));
            }
            if (revealType === LeadRevealType.PersonalEmail) {
              setPersonalEmailRevealingLeads((prev) => ({
                ...prev,
                [leadId]: true,
              }));
            }
            // Use setTimeout to ensure state update is committed before starting polling
            setTimeout(() => {
              handleFetchLeadsAfterBulkActions(true, true);
            }, 0);
          }
        }
      } catch (e: any) {
        if (
          revealType === LeadRevealType.Email ||
          revealType === LeadRevealType.EmailPersonalEmail
        ) {
          setEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            delete updated[leadId];
            return updated;
          });
        }
        if (
          revealType === LeadRevealType.EmailPhone ||
          revealType === LeadRevealType.EmailPersonalEmailPhone
        ) {
          setEmailPhoneRevealingLeads((prev) => {
            const updated = { ...prev };
            delete updated[leadId];
            return updated;
          });
        }
        if (
          revealType === LeadRevealType.PersonalEmail ||
          revealType === LeadRevealType.EmailPersonalEmail ||
          revealType === LeadRevealType.EmailPersonalEmailPhone
        ) {
          setPersonalEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            delete updated[leadId];
            return updated;
          });
        }
        const backendMessage = e?.message;
        toaster.error(
          isInsufficientCreditsError(backendMessage)
            ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
            : backendMessage || SystemErrors.InternalServerError,
          {
            theme: Theme.New,
          },
        );
      }
    },
    [
      handleFetchLeadsAfterBulkActions,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setPersonalEmailRevealingLeads,
      setLeadFinderRevealType,
    ],
  );

  // Bulk reveal
  const handleBulkReveal = useCallback(
    async (
      revealType: LeadRevealType,
      tagIds: number[] = [],
      newTags: string[] = [],
    ) => {
      try {
        setLeadFinderRevealType(revealType);

        if (isSelectedAllLeads) {
          // Set selectAllRevealType immediately before API call
          setSelectAllRevealType(revealType);

          const generatedPayload = handleGeneratePayload();
          const leadsFilterPayload =
            generatedPayload && typeof generatedPayload === 'object'
              ? generatedPayload
              : {};

          const totalRows = leadsData?.pagination?.totalRows || 0;
          const maxLimit =
            activeSubTab === LeadFinderSecondaryTab.SAVED
              ? SAVED_TAB_BULK_OPERATION_LIMIT
              : leadFinderLimit;
          const baseCount = Math.min(maxLimit, totalRows);

          const payload: any = {
            leadsFilter: {
              ...leadsFilterPayload,
              tab: activeSubTab,
              page: totalRows > leadFinderLimit ? 1 : apiParam?.page,
            },
            revealType,
            tagIds: tagIds?.length > 0 ? tagIds : [],
            newTags: newTags?.length > 0 ? newTags : [],
            leadsToBeRevealedCount: baseCount,
          };

          // Add optional fields only if they have values
          if (deSelectedLeads && deSelectedLeads.length > 0) {
            payload.deSelectedLeadIds = deSelectedLeads;
          }
          if (peopleMaxLeadsLimit != null) {
            payload.maxLeadsToBeProcessed = peopleMaxLeadsLimit;
          }

          const revealRes = await bulkRevealLeadAPI(payload);
          if (revealRes) {
            const { message, status } = revealRes.payload || {};
            if (status === 0) {
              toaster.error(
                isInsufficientCreditsError(message)
                  ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                  : message || SystemErrors.InternalServerError,
                {
                  theme: Theme.New,
                },
              );
              setSelectAllRevealType(null);
            } else if (status === 2) {
              toaster.warning(
                message ||
                  'Your another bulk lead reveal action is in progress. Please wait until it finishes',
                { theme: Theme.New },
              );
              setSelectAllRevealType(null);
            } else {
              const notificationUuId =
                revealRes?.payload?.notificationData?.notificationUuId;

              if (notificationUuId) {
                // Add to Firebase tracking set — all operations tracked via Firebase
                setFirebaseNotificationUuIds((prev) =>
                  new Set(prev).add(notificationUuId),
                );
                // Disable polling — Firebase handles all notifications
                dispatch(disableBulkActionRequestPolling());
                // Pre-populate so header filter works immediately (before Firebase snapshot arrives)
                dispatch(
                  setFirebaseProgressNotification({
                    uuid: notificationUuId,
                    notification: {
                      uuid: notificationUuId,
                      notificationType: getNotificationTypeForReveal(revealType),
                      onScreen: true,
                      metaData: {
                        totalCount:
                          revealRes?.payload?.notificationData?.totalLeads || 0,
                        completedCount: 0,
                        failedCount: 0,
                      },
                    },
                  }),
                );
              } else if (revealRes?.payload?.shouldPoll) {
                // Fallback to polling if no notificationUuId
                dispatch(enableBulkActionRequestPolling());
              }

              toaster.success(
                message ||
                  'Bulk reveal for leads are started. This can take few moments, You will be notified once the process is completed.',
                { theme: Theme.New },
              );
              // Set ref to true initially since we just triggered reveal
              hasRevealingLeadsRef.current = true;
              // Prime page-awareness so the first Firebase progress event
              // isn't blocked while the initial refetch is still in flight.
              hasRevealingLeadsOnCurrentPageRef.current = true;
              // Always seed leadsData with the server-side `isRevealing: true`
              // flags right after starting the reveal — Firebase or polling.
              setTimeout(() => {
                handleFetchLeadsAfterBulkActions(!notificationUuId, true);
              }, 0);
            }
          }
        } else {
          // For individual selection, use leadIds
          const leadIds: number[] = selectedLeads ?? [];

          // Set loading states
          if (revealType === LeadRevealType.Email) {
            setEmailRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(leadIds.map((lead) => [lead, true])),
            }));
          }
          if (revealType === LeadRevealType.EmailPhone ||
              revealType === LeadRevealType.EmailPersonalEmailPhone) {
            setEmailPhoneRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(leadIds.map((lead) => [lead, true])),
            }));
          }
          if (revealType === LeadRevealType.PersonalEmail ||
              revealType === LeadRevealType.EmailPersonalEmail ||
              revealType === LeadRevealType.EmailPersonalEmailPhone) {
            setPersonalEmailRevealingLeads((prev) => ({
              ...prev,
              ...Object.fromEntries(leadIds.map((lead) => [lead, true])),
            }));
          }

          const payload = {
            leadIds,
            revealType,
            tagIds: tagIds.length > 0 ? tagIds : [],
            newTags: newTags.length > 0 ? newTags : [],
          };

          const revealRes = await bulkRevealLeadAPI(payload);
          if (revealRes) {
            const { message, status } = revealRes.payload || {};
            if (status === 0) {
              toaster.error(
                isInsufficientCreditsError(message)
                  ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
                  : message || SystemErrors.InternalServerError,
                {
                  theme: Theme.New,
                },
              );
              // Clear loading states on error
              if (revealType === LeadRevealType.Email) {
                setEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.EmailPhone ||
                  revealType === LeadRevealType.EmailPersonalEmailPhone) {
                setEmailPhoneRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.PersonalEmail ||
                  revealType === LeadRevealType.EmailPersonalEmailPhone) {
                setPersonalEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
            } else if (status === 2) {
              toaster.warning(
                message ||
                  'Your another bulk lead reveal action is in progress. Please wait until it finishes',
                { theme: Theme.New },
              );
              // Clear loading states on warning
              if (revealType === LeadRevealType.Email) {
                setEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.EmailPhone ||
                  revealType === LeadRevealType.EmailPersonalEmailPhone) {
                setEmailPhoneRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
              if (revealType === LeadRevealType.PersonalEmail ||
                  revealType === LeadRevealType.EmailPersonalEmailPhone) {
                setPersonalEmailRevealingLeads((prev) => {
                  const updated = { ...prev };
                  leadIds.forEach((id) => {
                    delete updated[id];
                  });
                  return updated;
                });
              }
            } else {
              const notificationUuId =
                revealRes?.payload?.notificationData?.notificationUuId;

              if (notificationUuId) {
                // Add to Firebase tracking set — all operations tracked via Firebase
                setFirebaseNotificationUuIds((prev) =>
                  new Set(prev).add(notificationUuId),
                );
                // Disable polling — Firebase handles all notifications
                dispatch(disableBulkActionRequestPolling());
                // Pre-populate so header filter works immediately (before Firebase snapshot arrives)
                dispatch(
                  setFirebaseProgressNotification({
                    uuid: notificationUuId,
                    notification: {
                      uuid: notificationUuId,
                      notificationType: getNotificationTypeForReveal(revealType),
                      onScreen: true,
                      metaData: {
                        totalCount:
                          revealRes?.payload?.notificationData?.totalLeads || 0,
                        completedCount: 0,
                        failedCount: 0,
                      },
                    },
                  }),
                );
              } else if (revealRes?.payload?.shouldPoll) {
                // Fallback to polling if no notificationUuId
                dispatch(enableBulkActionRequestPolling());
              }

              toaster.success(
                message ||
                  'Bulk reveal for leads are started. This can take few moments, You will be notified once the process is completed.',
                { theme: Theme.New },
              );
              // Set ref to true initially since we just triggered reveal
              hasRevealingLeadsRef.current = true;
              // Prime page-awareness so the first Firebase progress event
              // isn't blocked while the initial refetch is still in flight.
              hasRevealingLeadsOnCurrentPageRef.current = true;
              // Always seed leadsData with the server-side `isRevealing: true`
              // flags right after starting the reveal — Firebase or polling.
              handleFetchLeadsAfterBulkActions(!notificationUuId, true);
            }
          }
        }

        // Clear selection after reveal
        setIsSelectedAllLeads(false);
        setSelectAllRevealType(null);
        setSelectedLeads([]);
        setDeSelectedLeads([]);
      } catch (e: any) {
        const backendMessage = e?.message;
        toaster.error(
          isInsufficientCreditsError(backendMessage)
            ? INSUFFICIENT_CREDITS_ERROR_MESSAGE
            : backendMessage || SystemErrors.InternalServerError,
          {
            theme: Theme.New,
          },
        );
        // Clear loading states on error
        if (revealType === LeadRevealType.Email) {
          setEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            selectedLeads.forEach((id) => {
              delete updated[id];
            });
            return updated;
          });
        }
        if (revealType === LeadRevealType.EmailPhone ||
            revealType === LeadRevealType.EmailPersonalEmailPhone) {
          setEmailPhoneRevealingLeads((prev) => {
            const updated = { ...prev };
            selectedLeads.forEach((id) => {
              delete updated[id];
            });
            return updated;
          });
        }
        if (revealType === LeadRevealType.PersonalEmail ||
            revealType === LeadRevealType.EmailPersonalEmailPhone) {
          setPersonalEmailRevealingLeads((prev) => {
            const updated = { ...prev };
            selectedLeads.forEach((id) => {
              delete updated[id];
            });
            return updated;
          });
        }
      }
    },
    [
      isSelectedAllLeads,
      deSelectedLeads,
      selectedLeads,
      leadsData,
      handleGeneratePayload,
      handleFetchLeadsAfterBulkActions,
      dispatch,
      setIsSelectedAllLeads,
      setSelectedLeads,
      setDeSelectedLeads,
      setEmailRevealingLeads,
      setEmailPhoneRevealingLeads,
      setLeadFinderRevealType,
      setSelectAllRevealType,
      activeSubTab,
      leadFinderLimit,
      peopleMaxLeadsLimit,
    ],
  );

  const refreshLeadsAndStartPolling = useCallback(() => {
    hasRevealingLeadsRef.current = true;
    handleFetchLeadsAfterBulkActions(true, true);
  }, [handleFetchLeadsAfterBulkActions]);

  // Used by save-to-list flow to register a Firebase notification.
  // Mirrors handleBulkReveal's Firebase path so leadsData gets seeded with
  // isRevealing flags and the page-awareness gate is open from the start —
  // otherwise Firebase progress events can't trigger throttled lead refetches.
  // Idempotent on notificationUuId: callers in listManagementUtils and
  // handleAddToList both invoke this for the same uuid.
  const addFirebaseNotification = useCallback(
    (
      notificationUuId: string,
      revealType: LeadRevealType,
      totalLeads: number,
    ) => {
      if (registeredFirebaseUuIdsRef.current.has(notificationUuId)) {
        return;
      }
      registeredFirebaseUuIdsRef.current.add(notificationUuId);
      setFirebaseNotificationUuIds((prev) =>
        new Set(prev).add(notificationUuId),
      );
      dispatch(disableBulkActionRequestPolling());
      dispatch(
        setFirebaseProgressNotification({
          uuid: notificationUuId,
          notification: {
            uuid: notificationUuId,
            notificationType: getNotificationTypeForReveal(revealType),
            onScreen: true,
            metaData: {
              totalCount: totalLeads,
              completedCount: 0,
              failedCount: 0,
            },
          },
        }),
      );
      hasRevealingLeadsRef.current = true;
      hasRevealingLeadsOnCurrentPageRef.current = true;
      // Immediate search refresh (no polling) — seeds leadsData with the
      // server-side isRevealing flags so the listener's throttled refetches fire.
      handleFetchLeadsAfterBulkActions(false, true);
    },
    [dispatch, handleFetchLeadsAfterBulkActions],
  );

  return {
    handleRevealLead,
    handleBulkReveal,
    refreshLeadsAndStartPolling,
    addFirebaseNotification,
    isPollingEnabled,
    leadFinderRevealType,
    setLeadFinderRevealType,
    selectAllRevealType,
    firebaseNotificationUuIds,
  };
};
